import { formatCpmCount } from '../../../constants/centralProductMaster.constants';

export type PipelineBadge = 'live' | 'attention' | 'idle' | 'unknown';

interface HeroProps {
    heroTotal: number;
    asOfLabel?: string;
    /** Soft-refresh in flight (pipeline badge stays visible). */
    refreshing?: boolean;
    /** Soft-refresh failed; prior stats still shown. */
    refreshFailed?: boolean;
    /** Amber label when refreshFailed — e.g. "Couldn't refresh — showing 09:40 IST". */
    refreshFailedLabel?: string;
    pipeline?: PipelineBadge;
    onRefresh?: () => void;
}

function pipelineChrome(pipeline: PipelineBadge): { label: string; dot: string; glow: string } {
    if (pipeline === 'attention') {
        return {
            label: 'Pipeline needs attention',
            dot: '#FBBF24',
            glow: 'rgba(251,191,36,.28)',
        };
    }
    if (pipeline === 'idle') {
        return {
            label: 'Pipeline idle',
            dot: 'rgba(255,255,255,.45)',
            glow: 'rgba(255,255,255,.12)',
        };
    }
    if (pipeline === 'unknown') {
        return {
            label: 'Pipeline status unavailable',
            dot: '#FBBF24',
            glow: 'rgba(251,191,36,.2)',
        };
    }
    return {
        label: 'Pipeline live',
        dot: '#4ADE80',
        glow: 'rgba(74,222,128,.25)',
    };
}

export default function Hero({
    heroTotal,
    asOfLabel,
    refreshing = false,
    refreshFailed = false,
    refreshFailedLabel,
    pipeline = 'live',
    onRefresh,
}: HeroProps) {
    const chrome = pipelineChrome(pipeline);
    const refreshDisabled = refreshing || onRefresh == null;

    return (
        <div
            className="rounded-[16px] flex items-end justify-between flex-wrap"
            style={{
                backgroundImage: 'var(--cpm-gradient-hero)',
                boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.28)',
                padding: '26px 28px',
                gap: 32,
            }}
        >
            <div className="flex flex-col max-w-[640px]" style={{ gap: 8 }}>
                <div
                    className="uppercase"
                    style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        color: 'rgba(255,255,255,.7)',
                    }}
                >
                    Central Product Master
                </div>
                <h1 className="m-0 !text-[30px] !font-extrabold !tracking-[-0.025em] !leading-[1.05] !text-white">
                    Overview
                </h1>
                <p
                    className="m-0 !text-[14px] !font-medium !leading-[1.5]"
                    style={{ color: 'rgba(255,255,255,.72)' }}
                >
                    One canonical catalog behind every store brand — master records, incoming tenant
                    links, and the jobs that build them.
                </p>
            </div>
            <div className="flex items-center flex-wrap" style={{ gap: 20 }}>
                <div className="flex flex-col" style={{ gap: 5 }}>
                    <div
                        className="uppercase"
                        style={{
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: '0.08em',
                            color: 'rgba(255,255,255,.6)',
                        }}
                    >
                        Master records
                    </div>
                    <div
                        className="tabular text-white"
                        style={{
                            fontSize: 38,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            lineHeight: 1,
                        }}
                    >
                        {formatCpmCount(heroTotal)}
                    </div>
                </div>
                <div
                    className="self-stretch"
                    style={{ width: 1, background: 'rgba(255,255,255,.18)' }}
                />
                <div className="flex flex-col" style={{ gap: 8 }}>
                    <div
                        className="inline-flex items-center rounded-full text-white"
                        style={{
                            gap: 7,
                            padding: '5px 11px',
                            background: 'rgba(255,255,255,.12)',
                            border: '1px solid rgba(255,255,255,.2)',
                            fontSize: 11.5,
                            fontWeight: 800,
                            width: 'fit-content',
                        }}
                    >
                        <span
                            style={{
                                width: 7,
                                height: 7,
                                borderRadius: 9999,
                                background: chrome.dot,
                                boxShadow: `0 0 0 3px ${chrome.glow}`,
                            }}
                        />
                        {chrome.label}
                    </div>
                    <div className="flex items-center" style={{ gap: 9 }}>
                        {refreshing ? (
                            <span
                                className="inline-flex items-center"
                                style={{
                                    gap: 7,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'rgba(255,255,255,.8)',
                                }}
                            >
                                <span
                                    className="cpm-spin-w"
                                    style={{ width: 12, height: 12, borderWidth: 1.8 }}
                                />
                                Refreshing…
                            </span>
                        ) : refreshFailed ? (
                            <span
                                className="inline-flex items-center"
                                style={{ gap: 6, fontSize: 12, fontWeight: 700, color: '#FDE68A' }}
                            >
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                                {refreshFailedLabel ?? "Couldn't refresh"}
                            </span>
                        ) : (
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: 'rgba(255,255,255,.65)',
                                }}
                            >
                                {asOfLabel ?? 'Data as of today'}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={onRefresh}
                            disabled={refreshDisabled}
                            style={{
                                padding: '5px 11px',
                                borderRadius: 999,
                                background: 'rgba(255,255,255,.12)',
                                border: '1px solid rgba(255,255,255,.24)',
                                color: '#fff',
                                fontFamily: 'inherit',
                                fontSize: 11.5,
                                fontWeight: 800,
                                cursor: refreshDisabled ? 'not-allowed' : 'pointer',
                                opacity: refreshDisabled ? 0.5 : 1,
                            }}
                        >
                            Refresh
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
